// @flow
/* eslint-disable react/no-access-state-in-setstate */
import React, { Component } from 'react';
import { debounce, isEmpty } from 'lodash';
import superagent from 'superagent';
import { Redirect } from 'react-router-dom';
import {
  findPlaceFromQuery,
  getPlacePredictions,
  getUkLatLngBounds,
  loadGoogle
} from '../../../helpers/maps';
import searchLocationId from './searchLocationId';
import SearchComponentInput from './SearchComponentInput';
import SearchResultsContainer from './SearchResultsContainer';
import GoogleDetails from './GoogleDetails';
import { track } from '../../../helpers/Tracking/Analytics';
import type { Google } from '../../Map/types';
import Api from '../../../api/Api';

import '../../../../static/scss/components/predictiveSearchInput.scss';

const debug = require('debug')('jp:searchInput');

type Props = {
  value?: string,
  onChangeHandler?: (...args: Array<any>) => any,
  isValid?: boolean,
  isHidden?: boolean,
  highlight?: boolean,
  placeholder?: string,
  browser?: any,
  autoLookup?: boolean,
  propagateEvents?: boolean,
  initialCoords?: any,
  placeCallback?: (...args: Array<any>) => any,
  initialState?: any,
  showGeoLocation?: boolean,
  showGoogle?: boolean,
  showDriveup?: boolean,
  showPostcode?: boolean,
  label?: string | boolean,
  country?: string,
  syncPropToState?: boolean,
  theme?: string
};

type State = {
  inputValue: string,
  query: string,
  activeIndex: boollean,
  results: Array<any>,
  redirect: boolean
};

class SearchInput extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      inputValue: this.props.value,
      query: '',
      activeIndex: false,
      results: [],
      redirect: false,
      // For storybook
      ...props.initialState
    };

    this.client = new Api(null, superagent);
    this.sessionToken = null;

    this.resultItemRef = React.createRef();
    this.resultsScrollContainer = React.createRef();
  }

  componentDidMount() {
    if (!isEmpty(this.props.initialCoords) || this.props.value === '') {
      return;
    }

    this.googleSearch(this.props.value).then(results => {
      if (!isEmpty(results)) {
        this.setState({
          query: this.props.value,
          googlePlaceId: results[0].place_id
        });
      }
    });
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.autoLookup &&
      nextProps.value !== this.state.inputValue &&
      nextProps.value.length
    ) {
      this.search(nextProps.value);
    }

    if (
      nextProps.syncPropToState &&
      nextProps.value !== this.state.inputValue
    ) {
      this.setState({ inputValue: nextProps.value });
    }
  }

  componentWillUpdate(nextProps) {
    if (
      nextProps.showGoogle &&
      nextProps.value !== this.props.value &&
      nextProps.value.length
    ) {
      this.googleSearch(nextProps.value).then(results => {
        if (!isEmpty(results)) {
          this.setState({
            query: nextProps.value,
            googlePlaceId: results[0].place_id,
            inputValue: nextProps.value
          });
        }
      });
    }
  }

  componentDidUpdate() {
    // Always ensure the active item is visible in the scroll container
    if (this.resultsScrollContainer.current) {
      const { height } = this.resultItemRef.current.getBoundingClientRect();
      const scrollTop = this.resultsScrollContainer.current.getScrollTop();
      const clientHeight = this.resultsScrollContainer.current.getClientHeight();

      if ((this.state.activeIndex + 1) * height > clientHeight + scrollTop) {
        // Moving down
        const scroll = (this.state.activeIndex + 1) * height - clientHeight;
        this.resultsScrollContainer.current.scrollTop(scroll);
      } else if (scrollTop > this.state.activeIndex * height) {
        // Moving up
        this.resultsScrollContainer.current.scrollTop(
          this.state.activeIndex * height
        );
      }
    }
  }

  googleSearch(input) {
    return this.generateSessionToken()
      .then(() => getUkLatLngBounds())
      .then(bounds =>
        getPlacePredictions({
          input,
          bounds,
          sessionToken: this.sessionToken
        }).catch(err => {
          // If no prediction results fallback to a query search
          if (err.message === 'ZERO_RESULTS') {
            return findPlaceFromQuery(input).then(queryResults => {
              if (queryResults.length === 1) {
                // If only one fallback result found use the user entered search term
                return [
                  {
                    ...queryResults[0],
                    description: input
                  }
                ];
              }

              // Otherwise use the address
              return queryResults.map(result => ({
                ...result,
                description: result.formatted_address
              }));
            });
          }

          throw err;
        })
      );
  }

  generateSessionToken() {
    if (this.sessionToken) {
      return Promise.resolve(this.sessionToken);
    }

    return loadGoogle().then((g: Google) => {
      this.sessionToken = new g.maps.places.AutocompleteSessionToken();
    });
  }

  inputChange(evt) {
    const value = typeof evt === 'string' ? evt : evt.target.value;

    // Update state and pass search throttle as a callback
    this.setState({
      inputValue: value,
      query: value,
      activeIndex: false,
      googlePlaceId: false,
      justParkId: false
    });

    this.search(value);
  }

  onInputBlur() {
    const { activeIndex } = this.state;

    if (Number.isInteger(activeIndex)) {
      this.setResult(activeIndex);
    }

    this.setState({
      results: []
    });
  }

  onAddressFound(address, coords) {
    this.setState({
      results: [],
      inputValue: address
    });
    if (typeof this.props.onChangeHandler === 'function') {
      this.props.onChangeHandler(address, {
        coords
      });
    }
  }

  highlightResult(index) {
    const { results } = this.state;
    const { description } = results[index];
    return this.setState({
      activeIndex: index,
      inputValue: description
    });
  }

  onKeyDown(evt) {
    /**
     * Cycle the active index.
     * Active index is false when nothing is highlighted
     * Or the array index of an item in the results
     * @param direction
     */
    const cycleActiveIndex = direction => {
      const { results, activeIndex, query } = this.state;
      const resultCount = results.length;

      if (resultCount === 0) {
        return;
      }

      if (activeIndex === false) {
        const newIndex = direction ? 0 : resultCount - 1;
        this.highlightResult(newIndex);
        return;
      }

      const newIndex = activeIndex + direction;

      if (newIndex < 0) {
        this.setState({ activeIndex: 0 });
        return;
      }

      if (newIndex === resultCount) {
        this.setState({
          activeIndex: false,
          inputValue: query
        });
        return;
      }

      this.highlightResult(newIndex);
    };

    switch (evt.keyCode) {
      case 38:
        // Up arrow
        evt.preventDefault();
        cycleActiveIndex(-1);
        break;
      case 40:
        // Down arrow
        evt.preventDefault();
        cycleActiveIndex(1);
        break;
      case 13:
        // Enter Key
        if (this.state.activeIndex !== false) {
          evt.preventDefault();
          this.setResult(this.state.activeIndex);
        }

        if (
          !this.props.propagateEvents &&
          (this.state.googlePlaceId || this.state.justParkId)
        ) {
          evt.preventDefault();
          document.forms[0].submit();
        }

        break;
      case 27:
        // Escape Key
        if (this.state.activeIndex !== false) {
          const { query } = this.state;
          this.setState({
            inputValue: query,
            activeIndex: false,
            results: []
          });
        }
        break;
      default:
        // Do nothing
        break;
    }
  }

  setResult(index) {
    const result = this.state.results[index];
    const inputValue = result.description;

    this.setState(
      {
        inputValue,
        query: '',
        results: [],
        activeIndex: false,
        googlePlaceId: result.type === 'google' ? result.place_id : false,
        justParkId: result.type === 'driveup' ? result.place_id : false,
        pcaId: result.type === 'postcode' ? result.place_id : false,
        meta: result.meta
      },
      () => {
        if (typeof this.props.onChangeHandler === 'function') {
          this.props.onChangeHandler(inputValue, {
            googlePlaceId: this.state.googlePlaceId,
            justParkId: this.state.justParkId,
            pcaId: this.state.pcaId
          });
        }

        // After state and DOM has updated check if we are a JustPark location and submit the form
        if (this.state.justParkId !== false) {
          const isPoe = this.state.meta.payOnExit;
          const shouldRedirectToCheckout =
            window && this.props.browser.greaterThan.small === false && !isPoe;

          track(
            'click:search-autocomplete-location',
            {
              type: 'DRIVE-UP'
            },
            {},
            () => {
              if (shouldRedirectToCheckout) {
                this.setState({
                  redirect: `/checkout/${this.state.justParkId}`
                });
              }
            }
          );

          if (shouldRedirectToCheckout) {
            return;
          }

          if (document) {
            window.location.href = `/${this.state.justParkId}`;
          }
        }
      }
    );
  }

  get search() {
    // Class getter that will return debounced function
    const throttleTime = 250;
    const throttleOptions = {
      leading: false
    };

    if (this.searchThrottle !== undefined) {
      return this.searchThrottle;
    }
    const { showGoogle, showDriveup, showPostcode } = this.props;

    let latestSearchedInput;

    this.searchThrottle = debounce(
      input => {
        // Store this input as the latest string searched
        latestSearchedInput = input;

        if (!input.length) {
          return this.setState({
            results: []
          });
        }
        // We only want to display results once both the google maps and JustPark
        // listing searches have completed. So we will use Promise.all, however if
        // one of the promises rejects and the other resolves we still want to show
        // some data.
        // So we wrap each call in a Promise that resolves to an empty array on rejection.

        const googlePromise = showGoogle
          ? new Promise(resolve =>
              this.googleSearch(input)
                .then(resolve)
                .catch(() => resolve([]))
            )
          : Promise.resolve([]);

        const justparkPromise = showDriveup
          ? new Promise(resolve =>
              searchLocationId(input)
                .then(resolve)
                .catch(() => resolve([]))
            )
          : Promise.resolve([]);

        const pcsPromise = showPostcode
          ? this.client.postcodeLookup(this.props.country, input)
          : Promise.resolve([]);

        return Promise.all([justparkPromise, googlePromise, pcsPromise])
          .then(values => {
            const driveupItems = values[0].map(item => ({
              html: `<b>${item.id}:</b> ${item.title}`,
              description: `${item.id}: ${item.title}`,
              place_id: item.id,
              type: 'driveup',
              meta: {
                payOnExit: item.payOnExit
              }
            }));

            const googleItems = values[1].map(item => {
              const str = item.description;
              let result = str;

              if (item.matched_substrings) {
                const { length, offset } = item.matched_substrings[0];

                result = `${str.substr(0, offset)}<b>${str.substr(
                  offset,
                  length
                )}</b>${str.substr(offset + length)}`;
              }

              return {
                html: result,
                description: item.description,
                place_id: item.place_id,
                type: 'google'
              };
            });

            const pcaItems = values[2].map(item => ({
              html: `${item.text}`,
              description: `${item.text}`,
              place_id: item.id,
              type: 'postcode'
            }));

            const shownDriveUpItems = driveupItems.slice(0, 3);
            const results = shownDriveUpItems
              .concat(googleItems.slice(0, 5 - shownDriveUpItems.length))
              .concat(pcaItems);

            if (latestSearchedInput !== input) {
              return null;
            }

            // Allow users to search first result on enter
            return this.setState({
              results,
              activeIndex: 0
            });
          })
          .catch(e => debug(e));
      },
      throttleTime,
      throttleOptions
    );

    return this.searchThrottle;
  }

  render() {
    const baseClassName = 'c-predictive-search-input__container';
    const themeClass = this.props.theme
      ? `c-predictive-search-input--${this.props.theme}-theme`
      : '';
    const isValidClass = this.props.isValid
      ? ''
      : `${baseClassName}--failed-validation`;

    const shouldHighlightClass = !this.props.highlight
      ? ''
      : `${baseClassName}--will-highlight`;

    const isHiddenClass = this.props.isHidden ? `${baseClassName}__hidden` : '';
    const googleClass = this.props.showGoogle
      ? `c-predictive-search-input__show_google`
      : '';

    const { label } = this.props;
    const { redirect } = this.state;

    const showGeolocation = this.props.showGeoLocation;

    return (
      <div
        className={`${baseClassName} ${
          this.state.results.length ? `${baseClassName}--has-results` : ''
        } ${isValidClass} ${shouldHighlightClass} ${isHiddenClass} ${googleClass} ${themeClass}`}
        data-cy={baseClassName}
      >
        {redirect && <Redirect to={redirect} push />}
        <SearchComponentInput
          value={this.state.inputValue}
          onChange={this.inputChange.bind(this)}
          onKeyDown={this.onKeyDown.bind(this)}
          onBlur={this.onInputBlur.bind(this)}
          placeholder={this.props.placeholder}
          onAddressFound={this.onAddressFound.bind(this)}
          showGeoLocation={showGeolocation}
          label={label}
        />
        {!!this.state.results.length &&
          (() => (
            <SearchResultsContainer
              results={this.state.results}
              activeIndex={this.state.activeIndex}
              onClickResult={this.setResult.bind(this)}
              onHoverResult={this.highlightResult.bind(this)}
              query={this.state.query}
              resultsScrollContainerRef={this.resultsScrollContainer}
              resultItemRef={this.resultItemRef}
            />
          ))()}
        {((this.props.showGoogle && this.state.googlePlaceId) ||
          !isEmpty(this.props.initialCoords)) && (
          <GoogleDetails
            placeId={this.state.googlePlaceId}
            initialCoords={this.props.initialCoords}
            callback={state => {
              this.props.placeCallback(state);
              // Now regen the session token
              this.generateSessionToken();
            }}
            sessionToken={this.sessionToken}
          />
        )}
        {this.state.justParkId && (
          <input
            type="hidden"
            name="jp_listing_id"
            value={this.state.justParkId}
          />
        )}
      </div>
    );
  }
}

SearchInput.defaultProps = {
  value: '',
  autoLookup: true,
  onChangeHandler: false,
  isValid: undefined,
  isHidden: false,
  highlight: false,
  placeholder: 'Where do you want to park?',
  browser: {
    greaterThan: {
      small: true
    },
    is: {
      extraSmall: false
    }
  },
  propagateEvents: false,
  initialCoords: {},
  placeCallback: () => {},
  initialState: {},
  showGeoLocation: true,
  showGoogle: true,
  showDriveup: true,
  showPostcode: false,
  label: 'Parking at',
  country: '',
  syncPropToState: false,
  theme: null
};

export default SearchInput;
